import React, { useEffect, useState, useRef } from 'react';

// API/Components
import {
	ANALYSIS_RESULT,
	validValue,
	isValidValue,
	AnalysisResultToStateColor,
	getMeasurementAnalysis,
} from '../enums';
import ResetZoomButton from '../reset_zoom_button';
import CustomGraphLegend from '../custom_graph_legend';

// Material UI
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';

// Chart.JS
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	Tooltip as ChartJSTooltip,
	BarController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from "tinycolor2";

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	ChartJSTooltip,
	BarController,
	zoomPlugin,
);

// String Realtime Graph Component
export default function StringRealtimeGraph(props) {

	const { stringsRealtimeData, limitData, graphScales, selectedString, setSelectedString, loading } = props;

	const [numStrings, setNumStrings] = useState(0);
	const [graphData, setGraphData] = useState({});
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		valuesVisible: false,
	});
	const [analyses, setAnalyses] = useState({
		"voltageAnalyses": null,
		"currentAnalyses": null,
	});
	const [graphOptions, setGraphOptions] = useState({});

	const chartRef = useRef(null);
	const selectedStringRef = useRef(selectedString);

	// Keep ref in sync with selectedString prop
	useEffect(() => {
		selectedStringRef.current = selectedString;
	}, [selectedString]);

	// Track number of strings - update when data changes structure
	useEffect(() => {
		const currentCount = stringsRealtimeData?.length || 0;
		if (currentCount !== numStrings) {
			setNumStrings(currentCount);
		}
	}, [stringsRealtimeData, numStrings]);

	// Update chart options only when graphScales, numStrings, or isInDischarge changes (preserves zoom/pan on data updates)
	useEffect(() => {
		// Use stringsRealtimeData length on initial render if numStrings not yet set
		const count = numStrings || stringsRealtimeData?.length || 0;
		if (count === 0) { return; }

		const options = createChartOptions(graphScales, limitData, graphSettings.limitsEnabled, count);
		setGraphOptions(options);
	// }, [graphScales, limitData, numStrings, stringsRealtimeData?.length, selectedString, setSelectedString]);
	}, [graphScales, limitData, numStrings, stringsRealtimeData?.length]);

	// Update annotations separately when limits toggle (preserves zoom/pan)
	useEffect(() => {
		const count = numStrings || stringsRealtimeData?.length || 0;
		if (!chartRef.current || count === 0) { return; }

		const annotations = buildAnnotations(limitData, graphSettings.limitsEnabled, count);
		if (chartRef.current.options?.plugins?.annotation) {
			chartRef.current.options.plugins.annotation.annotations = annotations;
			chartRef.current.update('none'); // Update without animation or reset
		}
	}, [graphSettings.limitsEnabled, limitData, numStrings]);

	// Process strings realtime data and update graph
	useEffect(() => {
		if (!stringsRealtimeData || stringsRealtimeData.length === 0) { return; }

		// Extract voltage and current arrays from all strings
		const voltages = stringsRealtimeData.map(s => validValue(s.voltage));
		const currents = stringsRealtimeData.map(s => validValue(s.current));

		// Get analysis results for colorization (if available per string)
		const voltageAnalyses = stringsRealtimeData.map(s => getMeasurementAnalysis(s.voltage, limitData?.volt?.hi_crit, limitData?.volt?.lo_crit));
		const currentAnalyses = stringsRealtimeData.map(s => getMeasurementAnalysis(s.current, limitData?.current?.string));

		setAnalyses({ "voltageAnalyses": voltageAnalyses, "currentAnalyses": currentAnalyses });

		// Create chart datasets with analysis-based colors
		let datasets = createChartDatasets(voltages, currents, voltageAnalyses, currentAnalyses);

		// Set chart data with string labels
		const data = {
			labels: stringsRealtimeData.map(s => `String ${s.string_id}`),
			datasets: datasets,
		};
		setGraphData(data);

	}, [stringsRealtimeData, limitData]);

	// Helper function to generate all dataset colors in a single pass
	function generateDatasetColors(stringsRealtimeData, voltageAnalyses, currentAnalyses, selectedString) {
		// Define alpha values for dimmed vs normal states
		const bgDimmedAlpha = 0.3;
		const bgNormalAlpha = 0.7;
		const borderDimmedAlpha = 0.3;
		const borderNormalAlpha = 1.0;

		// Define lighten/darken amounts for borders
		const voltageBorderLighten = 30;
		const currentBorderDarken = 20;
		
		// Intialize color arrays
		const voltageBg = [];
		const voltageBorder = [];
		const currentBg = [];
		const currentBorder = [];

		if (stringsRealtimeData && voltageAnalyses && currentAnalyses) {
			stringsRealtimeData.forEach((s, idx) => {
				const stringId = s.string_id;
				const isDimmed = selectedString !== 0 && selectedString !== stringId;
				
				const voltageColor = AnalysisResultToStateColor(voltageAnalyses[idx].result);
				const currentColor = AnalysisResultToStateColor(currentAnalyses[idx].result);

				const bgAlpha = isDimmed ? bgDimmedAlpha : bgNormalAlpha;
				const borderAlpha = isDimmed ? borderDimmedAlpha : borderNormalAlpha;
				
				voltageBg.push(tinycolor(voltageColor).setAlpha(bgAlpha).toRgbString());
				voltageBorder.push(tinycolor(voltageColor).lighten(voltageBorderLighten).setAlpha(borderAlpha).toRgbString());

				currentBg.push(tinycolor(currentColor).setAlpha(bgAlpha).toRgbString());
				currentBorder.push(tinycolor(currentColor).darken(currentBorderDarken).setAlpha(borderAlpha).toRgbString());
			});
		}

		return { voltageBg, voltageBorder, currentBg, currentBorder };
	}

	// Update only styling when selectedString changes (more efficient)
	useEffect(() => {
		if (!chartRef.current || !stringsRealtimeData || !analyses.voltageAnalyses || !analyses.currentAnalyses) {
			return;
		}

		const voltageDataset = chartRef.current.data.datasets[0];
		const currentDataset = chartRef.current.data.datasets[1];

		if (!voltageDataset || !currentDataset) return;

		// Generate all colors in a single pass
		const { voltageBg, voltageBorder, currentBg, currentBorder } = generateDatasetColors(
			stringsRealtimeData,
			analyses.voltageAnalyses,
			analyses.currentAnalyses,
			selectedString
		);

		// Update dataset styling
		voltageDataset.backgroundColor = voltageBg;
		voltageDataset.borderColor = voltageBorder;
		currentDataset.backgroundColor = currentBg;
		currentDataset.borderColor = currentBorder;

		// Update chart without animation
		chartRef.current.update('none');

	}, [selectedString, stringsRealtimeData, analyses]);

	// Handle bar click to select/deselect string
	function handleClickedBar(elements) {
		if (elements && elements.length > 0) {
			const clickedElement = elements[0];
			const clickedIndex = clickedElement.index;

			// Get the string_id from the clicked bar and set the selected string accordingly or set to 0 if same string clicked
			const clickedStringId = stringsRealtimeData[clickedIndex]?.string_id;
			if (clickedStringId !== undefined) {
				const currentSelected = selectedStringRef.current;
				setSelectedString(currentSelected === clickedStringId ? 0 : clickedStringId);
			}
		}
	}

	// Create datasets for voltage and current with analysis-based colors
	function createChartDatasets(voltages, currents, voltageAnalyses, currentAnalyses) {

		// Generate all colors in a single pass for efficiency
		const { voltageBg, voltageBorder, currentBg, currentBorder } = generateDatasetColors(
			stringsRealtimeData,
			voltageAnalyses,
			currentAnalyses,
			selectedString
		);

		// Create voltage dataset with individual colors per string
		const voltageDataset = {
			label: 'Voltage (V)',
			data: voltages,
			backgroundColor: voltageBg,
			borderColor: voltageBorder,
			borderWidth: 3,
			yAxisID: 'y-voltage',
			barPercentage: 0.9,
			xAxisID: 'x',
		};

		// Create current dataset with individual colors per string
		const currentDataset = {
			label: 'Current (A)',
			data: currents,
			backgroundColor: currentBg,
			borderColor: currentBorder,
			borderWidth: 3,
			yAxisID: 'y-current',
			barPercentage: 0.9,
			xAxisID: 'x',
		};

		return [voltageDataset, currentDataset];
	};


	// Build annotation objects for limit lines
	function buildAnnotations(limitData, limitsEnabled, numStrings) {
		if (!limitsEnabled || !limitData || !numStrings) {
			return {};
		}

		const annotations = {};

		// Get critical limit colors based on analysis results
		const highCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		const lowCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		// For each string, create limit lines positioned over its bars
		const barXMinOffset = 0.4;
		const barXMaxOffset = 0.005;
		
		for (let i = 0; i < numStrings; i++) {
			// X-coordinates for this string's bars
			const voltageBarXMin = i - barXMinOffset;
			const voltageBarXMax = i - barXMaxOffset;
			const currentBarXMin = i + barXMaxOffset;
			const currentBarXMax = i + barXMinOffset;

			// Voltage High Limit for this string
			if (limitData?.volt?.hi_crit !== undefined && limitData?.volt?.hi_crit !== null) {
				annotations[`voltageHiLimit_${i}`] = {
					type: 'line',
					xMin: voltageBarXMin,
					xMax: voltageBarXMax,
					xScaleID: 'x',
					yMin: limitData.volt.hi_crit,
					yMax: limitData.volt.hi_crit,
					yScaleID: 'y-voltage',
					borderColor: highCritColor,
					borderWidth: 2,
					borderDash: [5, 5],
					label: {
						content: 'Voltage High Limit',
						display: false,
					},
				};
			}

			// Voltage Low Limit for this string
			if (limitData?.volt?.lo_crit !== undefined && limitData?.volt?.lo_crit !== null) {
				annotations[`voltageLowLimit_${i}`] = {
					type: 'line',
					xMin: voltageBarXMin,
					xMax: voltageBarXMax,
					xScaleID: 'x',
					yMin: limitData.volt.lo_crit,
					yMax: limitData.volt.lo_crit,
					yScaleID: 'y-voltage',
					borderColor: lowCritColor,
					borderWidth: 2,
					borderDash: [5, 5],
					label: {
						content: 'Voltage Low Limit',
						display: false,
					},
				};
			}

			// Current High Limit for this string
			if (limitData?.current?.string !== undefined && limitData?.current?.string !== null) {
				annotations[`currentHiLimit_${i}`] = {
					type: 'line',
					xMin: currentBarXMin,
					xMax: currentBarXMax,
					xScaleID: 'x',
					yMin: Math.abs(limitData.current.string),
					yMax: Math.abs(limitData.current.string),
					yScaleID: 'y-current',
					borderColor: highCritColor,
					borderWidth: 2,
					borderDash: [5, 5],
					label: {
						content: 'Current High Limit',
						display: false,
					},
				};
			}
		}

		return annotations;
	};

	// Tooltip label formatting
	function setTooltipLabels(context) {
		const datasetLabel = context.dataset.label || '';
		const displayValue = context.parsed.y;

		// Return measurement with colored legend box
		if (datasetLabel.includes('Voltage')) {
			return ` Voltage: ${displayValue.toFixed(1)} VDC`;
		} else if (datasetLabel.includes('Current')) {
			return ` Current: ${displayValue.toFixed(1)} A`;
		} else {
			return `${datasetLabel}: ${displayValue.toFixed(2)}`;
		}
	}

	// Tooltip after label formatting for limits
	function setAfterTooltipLabel(context) {
		// Check if annotations exist to determine if limits are shown
		const hasAnnotations = context.chart?.options?.plugins?.annotation?.annotations && 
		Object.keys(context.chart.options.plugins.annotation.annotations).length > 0;
	
		if (!hasAnnotations || !limitData) { return []; }

		const lines = [];
		const datasetLabel = context.dataset.label || '';
		
		if (datasetLabel.includes('Voltage')) {
			// Show voltage limits after voltage measurement with dotted line indicators
			if (limitData?.volt?.hi_crit !== undefined && limitData?.volt?.hi_crit !== null) {
				lines.push(` Crit High: ${limitData.volt.hi_crit.toFixed(1)} VDC`);
			}
			if (limitData?.volt?.lo_crit !== undefined && limitData?.volt?.lo_crit !== null) {
				lines.push(` Crit Low: ${limitData.volt.lo_crit.toFixed(1)} VDC`);
			}
			lines.push(' ');
		} else if (datasetLabel.includes('Current')) {
			// Show current limit after current measurement with dotted line indicator
			if (limitData?.current?.string !== undefined && limitData?.current?.string !== null) {
				lines.push(` Crit High: ${Math.abs(limitData.current.string).toFixed(1)} A`);
			}
		}
		
		return lines;
	}

	// Create chart options configuration
	function createChartOptions(ranges, limitData, limitsEnabled, numStrings) {
		const {
			voltageDisplayMax,
			voltageDisplayMin,
			voltageBoundsMax,
			voltageBoundsMin,
			voltageMinRange,
			currentDisplayMax,
			currentDisplayMin,
			currentBoundsMax,
			currentBoundsMin,
			currentMinRange,
		} = ranges;

		const annotations = buildAnnotations(limitData, limitsEnabled, numStrings);

		return {
			maintainAspectRatio: false,
			animation: false,
			responsive: true,
			onClick: (event, elements, chart) => { handleClickedBar(elements); },
			interaction: {
				mode: 'index',
				intersect: false,
			},
			scales: {
				x: {
					grid: {
						display: false,
					},
				},
				'y-voltage': {
					type: 'linear',
					position: 'left',
					max: voltageDisplayMax,
					min: voltageDisplayMin,
					title: {
						display: true,
						text: 'Voltage (V)',
						font: {
							size: 14,
							weight: 'bold',
						},
					},
					grid: {
						color: 'rgba(0, 0, 0, 0.1)',
					},
				},
				'y-current': {
					type: 'linear',
					position: 'right',
					max: currentDisplayMax,
					min: currentDisplayMin,
					title: {
						display: true,
						text: 'Current (A)',
						font: {
							size: 14,
							weight: 'bold',
						},
					},
					grid: {
						drawOnChartArea: false,
					},
				},
			},
			plugins: {
				annotation: {
					annotations: annotations,
				},
				legend: {
					display: false,
				},
				tooltip: {
					filter: function(tooltipItem) {
						// Only show tooltip items that have actual values (not null)
						return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined;
					},
					callbacks: {
						label: setTooltipLabels,
						afterLabel: setAfterTooltipLabel,
					},
				},
				zoom: {
					pan: {
						enabled: true,
						mode: 'y',
					},
					limits: {
						'y-voltage': {
							max: voltageBoundsMax,
							min: voltageBoundsMin,
							minRange: voltageMinRange,
						},
						'y-current': {
							max: currentBoundsMax,
							min: currentBoundsMin,
							minRange: currentMinRange,
						},
					},
					zoom: {
						mode: 'y',
						wheel: {
							enabled: true,
							speed: 0.05,
						},
						pinch: {
							enabled: true,
						},
					},
				},
			},
		};
	};


	// Textual display of string voltage and current values
	const TextValuesDisplay = (
		<div style={{
			display: 'grid',
			gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
			gap: '3px',
			paddingLeft: '5px',
			paddingRight: '5px',
			borderRadius: '4px',
			marginTop: '2px',
			maxHeight: '80px',
			flexShrink: 0,
			overflowY: 'auto',
		}}>
			{stringsRealtimeData?.map((string, idx) => (
				<div key={string.string_id} style={{ 
					fontSize: '12px',
					fontWeight: 'bold',
					border: '1px solid #ddd',
					borderRadius: '3px',
					padding: '2px 4px',
				}}>
					<div style={{ marginBottom: '1px', textAlign: 'center', fontSize: '12px', textDecoration: 'underline' }}>
						String {string.string_id}
					</div>
					<div style={{ display: 'flex', justifyContent: 'space-evenly', gap: '4px' }}>
						<span style={{
							color: analyses?.voltageAnalyses
								? tinycolor(AnalysisResultToStateColor(analyses?.voltageAnalyses[idx]?.result || 0)).toHexString()
								: '#666'
						}}>
							V: {isValidValue(string.voltage) ? `${string.voltage.toFixed(1)} V` : 'Unknown'}
						</span>
						<span style={{
							color: analyses?.currentAnalyses
								? tinycolor(AnalysisResultToStateColor(analyses?.currentAnalyses[idx]?.result || 0)).toHexString()
								: '#666'
						}}>
							I: {isValidValue(string.current) ? `${Math.abs(string.current).toFixed(1)} A` : 'Unknown'}
						</span>
					</div>
				</div>
			))}
		</div>
	);

	const handleLimitMarkersToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			limitsEnabled: !prev.limitsEnabled,
		}));
	};

	const handleValuesToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			valuesVisible: !prev.valuesVisible,
		}));
	};

	const handleGraphResetZoom = () => {
		// Reset zoom/pan on both axes
		if (chartRef.current) {
			chartRef.current.resetZoom();
		}

		// Reset selected string
		setSelectedString(0);
	};

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				paddingLeft: '5px',
				paddingRight: '5px',
				paddingBottom: '3px',
				position: 'relative',
				overflow: 'hidden',
			}}
		>
			<div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center', flexShrink: 0 }}>
				<strong>Strings</strong>
			</div>
			<div style={{ 
				flex: '1 1 auto',
				width: '100%',
				minHeight: 0,
				overflow: 'hidden',
			}}>
				{ (graphData && graphData.datasets && graphData.datasets.length > 0) ? (
					<Chart
						ref={chartRef}
						type='bar'
						options={graphOptions}
						data={graphData}
					/>
				) : (
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
						color: '#666',
					}}>
						{loading ? 'Loading...' : 'No data available'}
					</div>
				)}
			</div>

			{ graphSettings.valuesVisible && TextValuesDisplay }

			<Grid container spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
				<Grid item>
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '2px' }}
					>
						<Tooltip title="Toggle limit markers on the graph" arrow>
							<ToggleButton
								value="limitMarkers"
								size="small"
								selected={graphSettings.limitsEnabled}
								onClick={handleLimitMarkersToggle}
								sx={{ 
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Limits
							</ToggleButton>
						</Tooltip>
						<Tooltip title="Toggle display of voltage and current values" arrow>
							<ToggleButton
								value="showValues"
								size="small"
								selected={graphSettings.valuesVisible}
								onClick={handleValuesToggle}
								sx={{ 
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Values
							</ToggleButton>
						</Tooltip>
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 0.5, columnGap: 2, padding: '4px 0' }}>
					<CustomGraphLegend datasets={graphData.datasets} />
				</Grid>
				<Grid item>
					<ResetZoomButton onClick={handleGraphResetZoom} />
				</Grid>
			</Grid>
		</div>
	);
}